import { NowPlayingIndicator } from '@/components/now-playing-indicator';
import useIsContentLocked from '@/hooks/use-is-content-locked';
import { useTrackRowPlayback } from '@/hooks/use-track-row-playback';
import {
    getGuideDuration,
    getGuideId,
    getGuideProgressDuration,
    getMediaType,
    MediaType,
} from '@/utils/media';
import { MediaGuideMetadata } from '@/utils/media/types';
import { EnumButtonName } from '@calm/evexia-analytics';
import { clamp } from '@calm/evexia-web-common';
import { FavoriteButton } from '@rhg/components/favorite-button';
import { useFavoriteGuide } from '@rhg/hooks/use-favorite-guide';
import { useHumanReadable } from '@rhg/hooks/use-human-readable';
import { ProgramContextState } from '@rhg/hooks/use-program-context/types';
import { useProvidedProgramContext } from '@rhg/hooks/use-provided-program-context';
import commonMessages from '@rhg/lang/messages/common';
import {
    Box,
    LinearProgress,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    SvgIcon,
    Theme,
    Typography,
} from '@rhg/libs/material';
import {
    DocumentGlyph,
    LockOutline,
    VideoFilled,
    Checkmark,
    CheckmarkCircleFilled,
    PlayCircleFilled,
} from '@rhg/libs/material/icons';
import { ProgramState } from '@rhg/utils/programs';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ScheduleCard } from '../horizontal-minimal-card';

const styleContainer = {
    borderBottom: 'solid 1px',
    borderColor: 'divider',
};
const styleProgress = {
    my: 2,
    mr: (theme: Theme) => theme.ds.spacingUnits.CONTENT,
};
const styleTools = { my: 2 };
const styleTool = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
};
const styleToolIcon = {
    color: 'text.sub',
    fontSize: 16,
    mr: 2,
};

const styleVideoIcon = (isCompleted: boolean) => ({
    ...(isCompleted
        ? {
              backgroundColor: (theme: Theme) =>
                  theme.palette.mode === 'light'
                      ? theme.palette.mask.white['80p']
                      : 'info.dark',
          }
        : {
              padding: (theme: Theme) => theme.ds.spacingUnits.BASE,
              color: 'primary.contrastText',
              backgroundColor: (theme: Theme) =>
                  theme.palette.mode === 'light'
                      ? theme.palette.mask.black['60p']
                      : theme.palette.mask.white['80p'],
          }),
    borderRadius: '50%',
    m: 2,
});

const styleVideoListItem = {
    marginBottom: (theme: Theme) => theme.ds.spacingUnits.CONTENT,
    paddingRight: (theme: Theme) => theme.ds.spacingUnits.SECTION,
};

const styleSecondaryAction = {
    display: 'flex',
    alignItems: 'center',
    gap: (theme: Theme) => theme.ds.spacingUnits.CONTENT,
};

interface GuideListItemProps {
    guide: MediaGuideMetadata;
    onClick?: (guide: MediaGuideMetadata) => void;
    onJoin?: (guide: MediaGuideMetadata) => void;
    programState: ProgramState;
    programType?: ProgramContextState['programType'];
}
export function GuideListItem({
    guide,
    onClick,
    onJoin,
    programState,
    programType,
}: GuideListItemProps) {
    const { formatMessage } = useIntl();
    const { toolsByGuide } = useProvidedProgramContext();
    const { roundedDuration } = useHumanReadable();
    const { getIsContentLocked } = useIsContentLocked();

    const isContentLocked = getIsContentLocked({
        programState,
        programType,
    });

    // The active row keeps its normal Play/complete icon and is signalled by a
    // right-aligned waveform indicator that animates while playing and freezes
    // in place when paused (per the playlist/album Figma). There is no per-row
    // Play/Pause toggle — the prominent program-level button owns play/pause.
    const { isActiveTrack, isPlaying } = useTrackRowPlayback(guide);

    const playGuide = useCallback(() => {
        onClick?.(guide);
    }, [guide, onClick]);

    const isVideo = useMemo(
        () => getMediaType(guide) === MediaType.Video,
        [guide]
    );

    const trackButton = useMemo<EnumButtonName>(() => {
        if (
            isContentLocked ||
            (programType === 'clinical_program' &&
                programState !== ProgramState.InProgress &&
                programState !== ProgramState.Completed)
        ) {
            return 'GuideItemLocked';
        } else {
            return 'GuideItemPlay';
        }
    }, [isContentLocked, programState, programType]);

    const iconType = useMemo(() => {
        if (
            isContentLocked ||
            (programType === 'clinical_program' &&
                programState !== ProgramState.InProgress &&
                programState !== ProgramState.Completed)
        ) {
            return 'locked';
        } else if (programType === 'clinical_program' && guide?.completed) {
            return 'completed';
        }
        if (isVideo) {
            return 'video';
        } else {
            return 'play';
        }
    }, [guide?.completed, isContentLocked, programState, programType, isVideo]);

    const icon = useMemo(() => {
        switch (iconType) {
            case 'locked':
                return <LockOutline />;
            case 'completed':
                if (isVideo) {
                    return <Checkmark size="large" color="info" />;
                }
                return <CheckmarkCircleFilled color="info" />;
            case 'video':
                return <VideoFilled sx={styleVideoIcon(false)} />;
            case 'play':
            default:
                return <PlayCircleFilled />;
        }
    }, [iconType, isVideo]);

    const iconLabel = useMemo(() => {
        switch (iconType) {
            case 'locked':
                return formatMessage(commonMessages['common.locked'], {
                    title: guide.title,
                });
            case 'completed':
                return formatMessage(commonMessages['common.completed'], {
                    title: guide.title,
                });
            case 'play':
            case 'video':
            default:
                return formatMessage(commonMessages['common.play'], {
                    title: guide.title,
                });
        }
    }, [iconType, formatMessage, guide.title]);

    const guideDuration = getGuideDuration(guide);
    const guideProgressDuration = getGuideProgressDuration(guide);
    const completionPercentage = useMemo(
        () =>
            clamp(
                Math.round(
                    ((guideProgressDuration ?? 0) / (guideDuration ?? 1)) * 100
                ),
                0,
                100
            ),
        [guideProgressDuration, guideDuration]
    );

    const guideId = getGuideId(guide);
    const guideTools = useMemo(
        () => toolsByGuide[guideId] ?? [],
        [guideId, toolsByGuide]
    );

    const { favoriteGuide, canFavorite } = useFavoriteGuide({
        onJoin,
        programType,
        programState,
    });

    if (isVideo) {
        return (
            <ListItem
                sx={styleVideoListItem}
                disablePadding
                secondaryAction={
                    <FavoriteButton
                        guide={guide}
                        canFavorite={canFavorite}
                        favoriteGuide={favoriteGuide}
                        size="legacyMedium"
                        trackButtonFavorite="GuideItemFavorite"
                        trackButtonUnfavorite="GuideItemUnfavorite"
                    />
                }
            >
                <ScheduleCard
                    onPlay={playGuide}
                    guide={guide}
                    bottomIconOverride={
                        <SvgIcon
                            aria-label={iconLabel}
                            sx={styleVideoIcon(iconType === 'completed')}
                        >
                            {icon}
                        </SvgIcon>
                    }
                />
            </ListItem>
        );
    }

    return (
        <ListItem
            sx={styleContainer}
            disablePadding
            secondaryAction={
                <Box sx={styleSecondaryAction}>
                    {isActiveTrack && (
                        <NowPlayingIndicator
                            title={guide.title}
                            isPlaying={isPlaying}
                        />
                    )}
                    <FavoriteButton
                        guide={guide}
                        canFavorite={canFavorite}
                        favoriteGuide={favoriteGuide}
                        size="legacyMedium"
                        trackButtonFavorite="GuideItemFavorite"
                        trackButtonUnfavorite="GuideItemUnfavorite"
                    />
                </Box>
            }
        >
            <ListItemButton
                onClick={playGuide}
                trackButton={trackButton}
                aria-current={isActiveTrack ? 'true' : undefined}
            >
                <ListItemIcon role="note" aria-label={iconLabel}>
                    {icon}
                </ListItemIcon>
                <ListItemText
                    secondary={formatMessage(commonMessages['common.min'], {
                        minutes: roundedDuration(guideDuration),
                    })}
                >
                    {`${guide.position}. ${guide?.title}`}
                    {programType === 'clinical_program' && guide?.completed && (
                        <Typography variant="srOnly">
                            {formatMessage(commonMessages['common.completed'], {
                                title: guide.title,
                            })}
                        </Typography>
                    )}
                    {guideTools.length > 0 && (
                        <Box sx={styleTools}>
                            {guideTools.map((tool, i) => (
                                <Box
                                    key={i}
                                    sx={styleTool}
                                    aria-label={formatMessage(
                                        commonMessages['common.tool'],
                                        {
                                            name: tool.title,
                                        }
                                    )}
                                >
                                    <DocumentGlyph sx={styleToolIcon} />
                                    <Typography
                                        variant="body2"
                                        color="text.sub"
                                        component="span"
                                    >
                                        {tool.title}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                    {!isActiveTrack &&
                    programType === 'clinical_program' &&
                    guideProgressDuration &&
                    guideDuration ? (
                        <LinearProgress
                            color="info"
                            variant="determinate"
                            sx={styleProgress}
                            aria-label={formatMessage(
                                commonMessages['common.percent_complete'],
                                {
                                    percentage: completionPercentage,
                                }
                            )}
                            value={completionPercentage}
                            withCurvedCorners
                        />
                    ) : null}
                </ListItemText>
            </ListItemButton>
        </ListItem>
    );
}
